import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Header, Button, Icon, Dropdown, Message } from 'semantic-ui-react';
import DeleteConfirmationModalWrapper from '../../../common/components/DeleteConfirmationModalWrapper';
import { v4 as uuidv4 } from 'uuid';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { setVariationSelection } from '../../slices/voiceActorDataSlice';

// ✅ NEW: Import stable hooks instead of direct zone hooks
import {
  useStableVariationManagement,
  useStableSharedData,
  useStableCharacterZone,
  useStableActorZone,
  useStablePortfolioZone
} from '../../hooks/useStableHooks';

// ✅ NEW: Import edit version manager and utilities
import useEditVersionManager from '../../hooks/useEditVersionManager_upgrade';
import { uploadCompleteVersion } from '../../slices/vaUtilitiesSlice';

import { VariationFormPanel } from '../forms';
import { VariationsListEnh } from '../lists';
import { getSignedUrlForS3Path } from '../../../utils/s3PathUtils';
import { useSignedUrl } from '../../../common/hooks/useSignedUrl';
// import { useMediaUpload } from '../../../hooks/useMediaUpload';
import '../../styles/voiceActorWorksheet.css';

/**
 * Enhanced Variation Management component for VoiceActor Worksheet
 * Manages variations for a selected character in a portfolio
 * 
 * @returns {JSX.Element} VariationManagementEnh component
 */
const VariationManagementEnh = () => {
  const userId = useSelector(state => state.user.profile?.data?.userId);
  const dispatch = useDispatch();

  // ✅ NEW: Use stable hooks instead of direct zone hooks
  const variationManagement = useStableVariationManagement();
  const sharedData = useStableSharedData();
  const characterZone = useStableCharacterZone();
  const actorZone = useStableActorZone();
  const portfolioZone = useStablePortfolioZone();

  // ✅ NEW: Use edit version manager for variation operations
  // Note: These are available but not used in this component yet
  // const {
  //   loadVariation,
  //   samples: editSamples,
  //   editVersionData,
  //   deleteVariation
  // } = useEditVersionManager();
  useEditVersionManager(); // Call the hook but don't destructure unused values

  // ✅ NEW: Use media upload hook for real audio uploads
  // const { uploadMedia } = useMediaUpload();

  // Extract data from stable hooks
  const {
    activeVariation,
    actions: variationActions
  } = variationManagement;

  const { characters, selectedCharacter, actions: characterActions } = characterZone;
  const { activePortfolio, actions: portfolioActions } = portfolioZone;
  const { selectedActor, actions: actorActions } = actorZone;
  const { selections, actors: sharedActors, portfolios: sharedPortfolios } = sharedData;

  // Get current selections
  const selectedActorData = sharedActors.find(a => a.uuid === selections.actorId) || selectedActor;
  const selectedPortfolioData = sharedPortfolios.find(p => p.uuid === selections.portfolioId) || activePortfolio;
  const selectedCharacterData = characters.find(c => c.uuid === selections.characterId) || selectedCharacter;
  const selectedVariation = activeVariation;

  // Extract IDs from selections
  const actorId = selectedActorData?.uuid;
  const portfolioId = selectedPortfolioData?.uuid;
  const characterId = selectedCharacterData?.uuid;
  const variationId = selectedVariation?.uuid;

  // Get relationship data
  const portfoliosForActor = sharedPortfolios.filter(p => p.actorId === actorId);
  const charactersForPortfolio = characters.filter(c => c.portfolioId === portfolioId);

  // UI state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // const [uploadStatus, setUploadStatus] = useState({
  //   loading: false,
  //   error: null,
  //   success: false
  // });

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  // const [pendingRecording, setPendingRecording] = useState(null);

  // Local state
  const [variationToDelete, setVariationToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingFormValues, setPendingFormValues] = useState({});

  // "Use as Base" functionality state
  const [selectedSampleForNewVariation, setSelectedSampleForNewVariation] = useState(null);
  const [isCreatingFromSample, setIsCreatingFromSample] = useState(false);

  // Create refs
  const formikRef = useRef();
  const variationListRefreshRef = useRef();
  const newVariationIdRef = useRef(uuidv4());

  // Update editing state when variation ID changes
  useEffect(() => {
    setIsEditing(!!variationId);
  }, [variationId, setIsEditing]);

  // Use the signed URL hook for audio playback
  const {
    signedAudioUrl,
    activeParams,
    updateSignedUrl
  } = useSignedUrl(selectedVariation, userId);

  // Use the media upload hook for audio uploads
  // const { uploadMedia } = useMediaUpload();

  // Reset upload status when selection changes
  // useEffect(() => {
  //   setUploadStatus({ loading: false, error: null, success: false });
  // }, [setUploadStatus, selectedVariation?.uuid, selectedCharacterData?.uuid, selectedPortfolioData?.uuid]);

  // Apply pending form values when formikRef becomes available
  useEffect(() => {
    if (formikRef.current && Object.keys(pendingFormValues).length > 0) {
      console.log('[VA_VARIATION_MGMT] Applying pending form values:', pendingFormValues);

      // Apply all pending form values
      Object.entries(pendingFormValues).forEach(([field, value]) => {
        if (field === 'selectionDuration') {
          // Special handling for selectionDuration
          if (formikRef.current.selectionDuration) {
            formikRef.current.selectionDuration.current = value;
          }
        } else {
          // Normal form fields
          formikRef.current.setFieldValue(field, value);
        }
      });

      // Clear pending values
      setPendingFormValues({});
    }
  }, [pendingFormValues]);

  // Use a ref to keep track of the last character ID we fetched variations for
  const lastFetchedCharacterId = useRef(null);

  // Fetch variations when characterId changes
  useEffect(() => {
    if (characterId && portfolioId && characterId !== lastFetchedCharacterId.current) {
      console.log(`[VA_VARIATION_MGMT] Character ID changed to ${characterId}, fetching variations`);
      // Update the ref before fetching to prevent infinite loops
      lastFetchedCharacterId.current = characterId;

      // Fetch variations for this character
      variationActions.fetchVariations(characterId, portfolioId);
    }
  }, [characterId, portfolioId, variationActions]);

  // Memoize dropdown options to prevent unnecessary re-renders
  const actorOptions = useMemo(() => {
    return selectedActorData ? [{
      key: selectedActorData.uuid,
      text: selectedActorData.name || 'Selected Actor',
      value: selectedActorData.uuid
    }] : [];
  }, [selectedActorData]);

  // Create dropdown options from all available portfolios
  const portfolioOptions = useMemo(() => {
    let options = (portfoliosForActor || []).filter(portfolio => portfolio && portfolio.uuid).map(portfolio => ({
      key: portfolio.uuid,
      text: portfolio.name || portfolio.title || 'Unnamed Portfolio',
      value: portfolio.uuid
    }));

    // If we have a selected portfolio but it's not in the options, add it
    if (selectedPortfolioData && !options.some(option => option.value === selectedPortfolioData.uuid)) {
      options.unshift({
        key: selectedPortfolioData.uuid,
        text: selectedPortfolioData.name || selectedPortfolioData.title || 'Selected Portfolio',
        value: selectedPortfolioData.uuid
      });
    }

    return options;
  }, [selectedPortfolioData, portfoliosForActor]);

  // Create dropdown options from all available characters
  const characterOptions = useMemo(() => {
    let options = (charactersForPortfolio || []).filter(character => character && character.uuid).map(character => ({
      key: character.uuid,
      text: character.name || 'Unnamed Character',
      value: character.uuid
    }));

    // If we have a selected character but it's not in the options, add it
    if (selectedCharacterData && !options.some(option => option.value === selectedCharacterData.uuid)) {
      options.unshift({
        key: selectedCharacterData.uuid,
        text: selectedCharacterData.name || 'Selected Character',
        value: selectedCharacterData.uuid
      });
    }

    return options;
  }, [charactersForPortfolio, selectedCharacterData]);

  // Handle new variation button click
  const handleNewVariationClick = useCallback(() => {
    dispatch(setVariationSelection(null));
    // Generate a new UUID for the new variation
    newVariationIdRef.current = uuidv4();
  }, [dispatch]);

  // Handle delete variation
  const handleDeleteVariation = useCallback((variation) => {
    setVariationToDelete(variation);
    setShowDeleteModal(true);
  }, [setShowDeleteModal]);

  // Handle confirm delete variation
  const confirmDeleteVariation = useCallback(async () => {
    if (!variationToDelete) return;

    try {
      // Delete the variation
      await variationActions.deleteVariation({ variationId: variationToDelete.uuid });

      // Close the modal
      setShowDeleteModal(false);
      setVariationToDelete(null);

      // If the deleted variation was selected, clear the selection
      if (selectedVariation && selectedVariation.uuid === variationToDelete.uuid) {
        dispatch(setVariationSelection(null));
      }

      // Refresh the variations list
      if (characterId && portfolioId) {
        variationActions.fetchVariations(characterId, portfolioId);
      }

      setSuccessMessage('Variation deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('[VA_VARIATION_MGMT] Error deleting variation:', error);
      setErrorMessage('Error deleting variation: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  }, [variationToDelete, variationActions, setShowDeleteModal, selectedVariation, dispatch, characterId, portfolioId]);

  // Handle actor selection
  const handleActorSelect = useCallback((value) => {
    actorActions.setActorSelection(value);
    dispatch(setVariationSelection(null));

    // Fetch portfolios for this actor
    portfolioActions.fetchPortfolios(value);
  }, [actorActions, portfolioActions, dispatch]);

  // Handle portfolio selection
  const handlePortfolioSelect = useCallback((value) => {
    portfolioActions.setPortfolioSelection(value);
    dispatch(setVariationSelection(null));

    // Fetch characters for this portfolio
    characterActions.fetchCharacters(value);
  }, [portfolioActions, characterActions, dispatch]);

  // Handle character selection
  const handleCharacterSelect = useCallback((value) => {
    characterActions.selectCharacter(value);
    dispatch(setVariationSelection(null));

    // Fetch variations for this character
    if (portfolioId) {
      variationActions.fetchVariations(value, portfolioId);
    }
  }, [characterActions, variationActions, dispatch, portfolioId]);

  // Handle variation selection
  const handleVariationSelect = useCallback(async (variation) => {
    if (variation && variation.uuid) {
      // Set the selection first
      dispatch(setVariationSelection(variation.uuid));

      console.log('[VA_VARIATION_MGMT] Loading variation:', variation.uuid);

      try {
        await variationActions.loadVariationForEdit({
          variationId: variation.uuid,
          characterId: characterId,
          actorId: actorId,
          portfolioId: portfolioId
        });
        console.log('[VA_VARIATION_MGMT] Successfully loaded variation');
        // Set editing mode to true after successful load
        setIsEditing(true);
      } catch (error) {
        console.error('[VA_VARIATION_MGMT] Error loading variation:', error);
        // Fallback to basic form population if loading fails
        if (formikRef.current && variation) {
          formikRef.current.setFieldValue('name', variation.name || '');
          formikRef.current.setFieldValue('content', variation.content || '');
          formikRef.current.setFieldValue('version', variation.version || 1);
          formikRef.current.setFieldValue('priority', variation.priority || 'medium');
          formikRef.current.setFieldValue('status', variation.status || 'not-started');
          formikRef.current.setFieldValue('deadline', variation.deadline || null);
        }

        setIsEditing(true);
      }
    }
  }, [dispatch, variationActions, characterId, actorId, portfolioId, setIsEditing]);

  // Handle "Use as Base" functionality
  const handleUseAsBase = useCallback((sample) => {
    console.log('[VA_VARIATION_MGMT] Use as Base triggered with sample:', sample);

    // Clear current variation selection
    dispatch(setVariationSelection(null));

    // Set the sample data for creating new variation
    setSelectedSampleForNewVariation(sample);
    setIsCreatingFromSample(true);

    // Generate a new UUID for the new variation
    newVariationIdRef.current = uuidv4();

    // Set editing mode to false since we're creating a new variation
    setIsEditing(false);
  }, [dispatch, setIsEditing]);

  // Handle cancel creating from sample
  const handleCancelCreateFromSample = useCallback(() => {
    console.log('[VA_VARIATION_MGMT] Cancel create from sample');

    // Clear the sample data
    setSelectedSampleForNewVariation(null);
    setIsCreatingFromSample(false);

    // Generate a new UUID for the next new variation
    newVariationIdRef.current = uuidv4();
  }, []);

  // ✅ NEW: Real audio upload implementation using VoiceActor utilities slice
  const uploadAudioForVariation = useCallback(async (audioBlob, variationId, metadata) => {
    try {
      console.log('[VA_VARIATION_MGMT] uploadAudioForVariation called with utilities slice');

      if (!variationId) {
        throw new Error('Variation ID is missing. Cannot upload audio.');
      }

      // setUploadStatus({ loading: true, error: null, success: false });

      const completeMetadata = {
        actorId: selectedActorData?.uuid,
        portfolioId: metadata.portfolioId || selectedPortfolioData?.uuid,
        characterId: metadata.characterId || selectedCharacterData?.uuid,
        variationName: metadata.variationName,
        fileDuration: metadata.fileDuration,
        characterName: selectedCharacterData?.name
      };

      const result = await dispatch(uploadCompleteVersion({
        audioBlob,
        textContent: metadata.content,
        variationId,
        metadata: completeMetadata
      })).unwrap();

      // setUploadStatus({ loading: false, error: null, success: true });
      return result.voice?.voiceS3Url;
    } catch (error) {
      console.error('[VA_VARIATION_MGMT] Error uploading audio:', error);
      // setUploadStatus({ loading: false, error: error.message, success: false });
      throw error;
    }
  }, [dispatch, selectedActorData, selectedPortfolioData, selectedCharacterData]);

  // Function to batch state updates after audio upload
  const batchedAudioPlayerUpdates = useCallback((url, s3Url) => {
    // Update the signed URL
    updateSignedUrl(url);

    // Update the form with the new s3Url
    if (formikRef.current) {
      formikRef.current.setFieldValue('s3Url', s3Url);

      // Reset the player completely if the resetPlayer function is available
      if (formikRef.current.resetPlayer && typeof formikRef.current.resetPlayer === 'function') {
        formikRef.current.resetPlayer();
      }
    }

    // Clear any pending recording
    // setPendingRecording(null);
  }, [updateSignedUrl]);

  // Handle save variation
  const handleSaveVariation = useCallback(async (values, inlineRecording) => {
    console.log('[VA_VARIATION_MGMT] handleSaveVariation called with values:', values);
    console.log('[VA_VARIATION_MGMT] inlineRecording:', inlineRecording);

    try {
      let result;
      let variationId;
      let newVariation;

      // Make sure we're not submitting a placeholder s3Url
      const submissionValues = { ...values };
      if (submissionValues.s3Url === 'Recording saved, will be uploaded when variation is created' ||
        submissionValues.s3Url === 'Recording saved, will be uploaded when variation is updated' ||
        submissionValues.s3Url === 'pending-recording') {
        submissionValues.s3Url = ''; // Reset to empty string instead of placeholder text
      }

      // Get the recording data (either from inline recording or pending recording)
      const recordingToUpload = inlineRecording;

      // Check if we have a recording with duration information
      if (recordingToUpload?.metadata?.fileDuration) {
        // Include the fileDuration in the submission values
        submissionValues.fileDuration = recordingToUpload.metadata.fileDuration;
        console.log('[VA_VARIATION_MGMT] Including fileDuration in submission:', recordingToUpload.metadata.fileDuration);
      }

      // Step 1: Create/update the variation in the database
      if (isEditing && selectedVariation) {
        // Update existing variation
        result = await variationActions.updateVariation({
          variationId: selectedVariation.uuid,
          data: submissionValues
        });

        variationId = selectedVariation.uuid;
        newVariation = result;

        // Refresh the variation list for updates too
        if (characterId) {
          variationActions.fetchVariations(characterId);
        }
      } else {
        // Check if we have a pending recording with a generated UUID
        const generatedUuid = recordingToUpload?.metadata?.variationId;

        // If we have a generated UUID from the recording, use it
        if (generatedUuid && typeof generatedUuid === 'string' &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(generatedUuid)) {
          // Include the UUID in the variation data
          submissionValues.uuid = generatedUuid;
        } else {
          // Otherwise, use the newVariationId
          submissionValues.uuid = newVariationIdRef.current;
        }

        // Create new variation
        result = await variationActions.createVariation({
          characterId: selectedCharacterData.uuid,
          data: submissionValues
        });

        // Check if result has the expected structure
        if (result && result.variation && result.variation.uuid) {
          // The result has the expected structure with a nested variation object
          variationId = result.variation.uuid;
          newVariation = result.variation;
        } else if (result && result.uuid) {
          // The result is the variation object directly
          variationId = result.uuid;
          newVariation = result;
        } else {
          // For now, we'll use the UUID we generated
          variationId = submissionValues.uuid;
          newVariation = {
            ...submissionValues,
            uuid: variationId
          };
        }

        // Generate a new UUID for the next new variation
        newVariationIdRef.current = uuidv4();

        // Refresh the variation list
        if (characterId && portfolioId) {
          variationActions.fetchVariations(characterId, portfolioId);
        }
      }

      // Step 2: If we have a recording, upload it using the valid variationId
      if (recordingToUpload && recordingToUpload.audioBlob && recordingToUpload.metadata) {
        try {
          // Double check that we have a valid variationId before proceeding
          if (!variationId) {
            throw new Error('Variation ID is missing. Cannot upload audio.');
          }

          // Validate the variationId is a valid UUID
          const isValidUuid = variationId && typeof variationId === 'string' &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(variationId);

          if (!isValidUuid) {
            throw new Error('Invalid variation ID format. Cannot upload audio.');
          }

          // Update metadata with the correct variationId
          const completeMetadata = {
            ...recordingToUpload.metadata,
            portfolioId: recordingToUpload.metadata.portfolioId || selectedPortfolioData?.uuid,
            characterId: recordingToUpload.metadata.characterId || selectedCharacterData?.uuid,
            variationId: variationId, // Use the validated variationId
            // Use the actual variation name from the form values
            variationName: values.name || recordingToUpload.metadata.variationName || 'Unnamed Variation',
            content: values.content // get content from form's Content Text* field
          };

          // Step 3: Upload the audio
          const s3Url = await uploadAudioForVariation(
            recordingToUpload.audioBlob,
            variationId,
            completeMetadata
          );

          // Step 4: Update the variation with the S3 URL
          if (s3Url) {
            // Get the current version from the variation object
            const currentVersion = newVariation.version || 1;

            // Create a new values object with the s3Url and the updated version
            const updatedValues = {
              ...submissionValues,
              s3Url,
              version: currentVersion + 1 // Increment the version
            };

            // Update the variation in the database
            await variationActions.updateVariation({
              variationId: variationId,
              data: updatedValues
            });

            // Update the signed URL for the audio player
            try {
              const url = await getSignedUrlForS3Path(s3Url, userId);
              setTimeout(() => batchedAudioPlayerUpdates(url, s3Url), 50);
            } catch (err) {
              console.error('[VA_VARIATION_MGMT] Error getting signed URL for uploaded recording:', err);
              updateSignedUrl('');
            }
          }
        } catch (uploadError) {
          console.error('[VA_VARIATION_MGMT] Error uploading audio after variation creation:', uploadError);
          // We don't want to fail the whole operation if just the audio upload fails
          // The variation has been created/updated successfully
        }
      }

      setSuccessMessage('Variation saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

      return true;
    } catch (error) {
      console.error('[VA_VARIATION_MGMT] Error submitting variation:', error);
      setErrorMessage('Error saving variation: ' + error.message);
      setTimeout(() => setErrorMessage(''), 5000);
      return false;
    }
  }, [
    isEditing,
    selectedVariation,
    selectedCharacterData,
    selectedPortfolioData,
    variationActions,
    characterId,
    portfolioId,
    uploadAudioForVariation,
    userId,
    updateSignedUrl,
    batchedAudioPlayerUpdates
  ]);

  return (
    <ManagementHeader
      title="Variation Management"
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <div>
              <div style={{ marginBottom: '1em' }}>
                <div style={{
                  marginTop: '0.6em',
                  position: 'relative'
                }}>
                  <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Voice Actor</label>
                  <Dropdown
                    selection
                    options={actorOptions}
                    value={actorId}
                    onChange={(e, { value }) => handleActorSelect(value)}
                    placeholder="Select Voice Actor"
                    fluid
                    style={{ zIndex: 1000 }}
                    disabled={actorOptions.length === 0}
                  />
                </div>
              </div>

              {actorId && (
                <div style={{ marginBottom: '1em' }}>
                  <div style={{
                    marginTop: '0.6em',
                    position: 'relative'
                  }}>
                    <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Portfolio</label>
                    <Dropdown
                      selection
                      options={portfolioOptions}
                      value={portfolioId}
                      onChange={(e, { value }) => handlePortfolioSelect(value)}
                      placeholder="Select Portfolio"
                      fluid
                      style={{ zIndex: 999 }}
                      disabled={portfolioOptions.length === 0}
                    />
                    {portfolioOptions.length === 0 && actorId && (
                      <Message info size="tiny" style={{ marginTop: '0.5em' }}>
                        <p>No portfolios found for this voice actor. Please create a portfolio first.</p>
                      </Message>
                    )}
                  </div>
                </div>
              )}

              {portfolioId && (
                <div style={{ marginBottom: '1em' }}>
                  <div style={{
                    marginTop: '0.6em',
                    position: 'relative'
                  }}>
                    <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Character</label>
                    <Dropdown
                      selection
                      options={characterOptions}
                      value={characterId}
                      onChange={(e, { value }) => handleCharacterSelect(value)}
                      placeholder="Select Character"
                      fluid
                      style={{ zIndex: 998 }}
                      disabled={characterOptions.length === 0}
                    />
                    {characterOptions.length === 0 && portfolioId && (
                      <Message info size="tiny" style={{ marginTop: '0.5em' }}>
                        <p>No characters found for this portfolio. Please create a character first.</p>
                      </Message>
                    )}
                  </div>
                </div>
              )}

              {selectedCharacterData && (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <Header as="h3" style={{ margin: 0 }}>Variations</Header>
                    <Button
                      primary
                      size="small"
                      icon
                      labelPosition="left"
                      onClick={handleNewVariationClick}
                      style={{ marginRight: '10px' }}
                    >
                      <Icon name="add" />
                      New Variation
                    </Button>
                  </div>

                  <VariationsListEnh
                    onVariationSelect={handleVariationSelect}
                    onDeleteVariation={handleDeleteVariation}
                    onVariationListRefreshRef={variationListRefreshRef}
                  />
                </div>
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={10}>
            {selectedCharacterData && (
              <>
                {successMessage && (
                  <Message positive>
                    <Message.Header>Success</Message.Header>
                    <p>{successMessage}</p>
                  </Message>
                )}
                <VariationFormPanel
                  // Form props
                  isEditing={isEditing}
                  formikRef={formikRef}
                  handleSubmit={handleSaveVariation}
                  // Entity props
                  selectedPortfolio={selectedPortfolioData}
                  selectedCharacter={selectedCharacterData}
                  selectedVariation={selectedVariation}
                  // Audio props
                  signedAudioUrl={signedAudioUrl}
                  activeParams={activeParams}
                  // Other props
                  newVariationId={newVariationIdRef.current}
                  setIsEditing={setIsEditing}
                  setShowDeleteModal={setShowDeleteModal}
                  onSampleDelete={() => {
                    // Trigger refresh of the variation list to update counts
                    if (variationListRefreshRef.current) {
                      variationListRefreshRef.current();
                    }
                  }}
                  // "Use as Base" functionality props
                  selectedSampleForNewVariation={selectedSampleForNewVariation}
                  isCreatingFromSample={isCreatingFromSample}
                  onUseAsBase={handleUseAsBase}
                  onCancelCreateFromSample={handleCancelCreateFromSample}
                />
              </>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModalWrapper
        open={showDeleteModal}
        variationToDelete={variationToDelete}
        onConfirm={confirmDeleteVariation}
        onCancel={() => {
          setShowDeleteModal(false);
          setVariationToDelete(null);
        }}
      />
    </ManagementHeader>
  );
};

export default VariationManagementEnh;
